import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Input, Button, Alert } from 'antd';
import lowerCase from 'lodash/lowerCase';

import { signup } from './actions';
import { selectIsSignupLoading, selectSignupError } from './selectors';
import PhoneCountryMask from '../address/PhoneCountryMask';
import { fetchCountries, addressSelectors } from '../address';

const RegisterForm = ({ onSwitchToLogin, onSuccess }) => {
    const dispatch = useDispatch();
    const isLoading = useSelector(selectIsSignupLoading);
    const serverError = useSelector(selectSignupError);

    const countries = useSelector((state) => addressSelectors.selectCountries(state));
    const isFetching = useSelector((state) => addressSelectors.selectIsCountriesFetching(state));

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [phone, setPhone] = useState('');
    const [phoneCountry, setPhoneCountry] = useState('RU');
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (!countries || !countries.countries) {
            dispatch(fetchCountries());
        }
    }, [dispatch]);

    let listPhoneCountries = [];
    if (!isFetching && countries && countries.countries) {
        listPhoneCountries = Object.values(countries.countries)
            .filter((el) => el.iso === 'RU')
            .map((element, index) => ({
                key: index,
                text: element.country_ru,
                flag: lowerCase(element.iso),
                value: element.iso,
                className: 'dropdown-item',
                phone_mask: element.phone_mask,
            }));
    }

    const handleChangeField = (fieldName, value) => {
        if (fieldName === 'phone_country') {
            setPhoneCountry(value);
        } else {
            setPhone(value);
            if (errors.phone) {
                setErrors((prev) => ({ ...prev, phone: undefined }));
            }
        }
    };

    const validate = () => {
        const newErrors = {};

        if (!email.trim()) {
            newErrors.email = 'Обязательно для заполнения';
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email)) {
                newErrors.email = 'Неверный формат email';
            }
        }

        if (!password) {
            newErrors.password = 'Обязательно для заполнения';
        } else if (password.length < 6) {
            newErrors.password = 'Минимум 6 символов';
        }

        if (password && password !== passwordConfirm) {
            newErrors.passwordConfirm = 'Пароли не совпадают';
        }

        if (!phone.trim() || phone.includes('_')) {
            newErrors.phone = 'Введите телефон полностью';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        const result = await dispatch(signup({
            phone,
            email,
            password,
            displayName: `${firstName} ${lastName}`.trim(),
            firstName,
            lastName,
        }));

        if (result?.success && onSuccess) {
            onSuccess();
        }
    };

    return (
        <div className="auth-form">
            {serverError && (
                <Alert
                    message={serverError}
                    type="error"
                    showIcon
                    closable
                    style={{ marginBottom: 16 }}
                />
            )}

            <div className="auth-fields">
                <div className="auth-field">
                    <label className="auth-label">Имя</label>
                    <Input
                        size="large"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="Иван"
                    />
                </div>

                <div className="auth-field">
                    <label className="auth-label">Фамилия</label>
                    <Input
                        size="large"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Иванов"
                    />
                </div>

                <div className="auth-field">
                    <label className="auth-label">Email <span className="auth-required">*</span></label>
                    <Input
                        size="large"
                        type="email"
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); if (errors.email) setErrors((prev) => ({ ...prev, email: undefined })); }}
                        placeholder="email@example.com"
                        status={errors.email ? 'error' : undefined}
                    />
                    {errors.email && <span className="auth-field-error">{errors.email}</span>}
                </div>

                <div className="auth-field">
                    <label className="auth-label">Пароль <span className="auth-required">*</span></label>
                    <Input.Password
                        size="large"
                        value={password}
                        onChange={(e) => { setPassword(e.target.value); if (errors.password) setErrors((prev) => ({ ...prev, password: undefined })); }}
                        placeholder="Минимум 6 символов"
                        status={errors.password ? 'error' : undefined}
                    />
                    {errors.password && <span className="auth-field-error">{errors.password}</span>}
                </div>

                <div className="auth-field">
                    <label className="auth-label">Подтверждение пароля <span className="auth-required">*</span></label>
                    <Input.Password
                        size="large"
                        value={passwordConfirm}
                        onChange={(e) => { setPasswordConfirm(e.target.value); if (errors.passwordConfirm) setErrors((prev) => ({ ...prev, passwordConfirm: undefined })); }}
                        placeholder="Повторите пароль"
                        status={errors.passwordConfirm ? 'error' : undefined}
                    />
                    {errors.passwordConfirm && <span className="auth-field-error">{errors.passwordConfirm}</span>}
                </div>

                <div className="auth-field">
                    <label className="auth-label">Телефон <span className="auth-required">*</span></label>
                    <PhoneCountryMask
                        extraClass="auth-phone-mask"
                        countries={listPhoneCountries}
                        country={phoneCountry}
                        loading={isFetching}
                        phone={phone}
                        handleChangeField={handleChangeField}
                        nameCountry="phone_country"
                        namePhone="phone"
                        fluid={true}
                    />
                    {errors.phone && <span className="auth-field-error">{errors.phone}</span>}
                </div>
            </div>

            <Button
                className="auth-submit-btn"
                size="large"
                block
                onClick={handleSubmit}
                loading={isLoading}
            >
                Зарегистрироваться
            </Button>

            <div className="auth-switch-text">
                Уже есть аккаунт?{' '}
                <a href="#" onClick={(e) => { e.preventDefault(); onSwitchToLogin(); }}>
                    Войти
                </a>
            </div>
        </div>
    );
};

export default RegisterForm;
